import React, { useState, useEffect, useRef } from 'react';
import spcLogo from '../spc-logo-transparent.png';

/* ─── COLORS ─── */
const C = {
  blue: '#6699ff',
  blueDark: '#4d7ae6',
  blueDeep: '#3a5fcc',
  blueGlow: 'rgba(102,153,255,0.15)',
  blueSub: 'rgba(102,153,255,0.06)',
  navy: '#0b1120',
  navyLight: '#111a2e',
  navyMid: '#162040',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  creamPale: '#f8f5ef',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#536179',
  white: '#ffffff',
  divider: 'rgba(102,153,255,0.12)',
};

/* ═══════════════════════════════════════════
   HOOKS
═══════════════════════════════════════════ */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => setVisible(e.isIntersecting),
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useScrolled(offset = 60) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > offset);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, [offset]);
  return scrolled;
}

function useMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [breakpoint]);
  return isMobile;
}

/* ═══════════════════════════════════════════
   SHARED COMPONENTS
═══════════════════════════════════════════ */
function Reveal({ children, delay = 0, className = '', style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: visible
          ? `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`
          : 'opacity 0.4s ease, transform 0.4s ease',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function RevealLeft({ children, delay = 0 }) {
  const [ref, visible] = useReveal();
  const isMobile = useMobile();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : `translateX(${isMobile ? '-20px' : '-60px'})`,
        transition: visible
          ? `opacity 0.9s ease ${delay}s, transform 0.9s ease ${delay}s`
          : 'opacity 0.4s ease, transform 0.4s ease',
      }}
    >
      {children}
    </div>
  );
}

function RevealRight({ children, delay = 0 }) {
  const [ref, visible] = useReveal();
  const isMobile = useMobile();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : `translateX(${isMobile ? '20px' : '60px'})`,
        transition: visible
          ? `opacity 0.9s ease ${delay}s, transform 0.9s ease ${delay}s`
          : 'opacity 0.4s ease, transform 0.4s ease',
      }}
    >
      {children}
    </div>
  );
}

function SectionTag({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: C.blue, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 32, height: 1, background: C.blue, display: 'inline-block' }} />
      {children}
    </div>
  );
}

function BtnPrimary({ children, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        padding: '18px 44px', background: C.blue, color: C.navy, border: 'none',
        fontFamily: "'Manrope',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 2,
        textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.4s ease',
        transform: h ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: h ? '0 16px 48px rgba(102,153,255,0.3)' : 'none',
        width: '100%',
      }}
    >{children}</button>
  );
}

function BtnGhost({ children, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        padding: '18px 36px', background: h ? 'rgba(102,153,255,0.05)' : 'transparent',
        color: h ? C.blue : C.cream,
        border: `1px solid ${h ? C.blue : 'rgba(245,240,232,0.2)'}`,
        fontFamily: "'Manrope',sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: 2,
        textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s ease',
        width: '100%',
      }}
    >{children}</button>
  );
}

function IndustryTag({ children }) {
  const [h, setH] = useState(false);
  return (
    <span
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        padding: '8px 20px', border: `1px solid ${h ? C.blue : C.creamWarm}`,
        fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
        color: h ? C.blue : C.textBody, background: h ? C.blueSub : C.white,
        transition: 'all 0.3s ease', cursor: 'default',
      }}
    >{children}</span>
  );
}

const scrollTo = (id) => (e) => {
  e?.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

/* ═══════════════════════════════════════════
   NAV
═══════════════════════════════════════════ */
function Nav() {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useMobile();

  const handleNavClick = (id) => (e) => {
    e?.preventDefault();
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav aria-label="Main navigation" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: isMobile ? '0 16px' : '0 24px',
        height: isMobile ? 80 : 130,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? 'rgba(245,240,232,0.95)' : C.creamLight,
        backdropFilter: scrolled ? 'blur(24px)' : 'none',
        borderBottom: `1px solid ${C.creamWarm}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        transition: 'all 0.4s ease',
      }}>
        <a href="#hero" onClick={handleNavClick('hero')} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img
            src={spcLogo}
            alt="Steel Process Consultancy Inc."
            style={{ height: isMobile ? 60 : 150, width: 'auto', objectFit: 'contain' }}
          />
        </a>

        {/* Desktop Links */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
            {[['About', 'about'], ['Expertise', 'expertise'], ['Services', 'services']].map(([label, id]) => (
            <NavLink key={id} href={`#${id}`} onClick={handleNavClick(id)}>{label}</NavLink>
            ))}
            <NavLink href="/articles" onClick={(e) => { e.preventDefault(); window.location.href = '/articles'; }}>Articles</NavLink>
            <NavLink href="/publications" onClick={(e) => { e.preventDefault(); window.location.href = '/publications'; }}>Publications</NavLink>
            <NavCTA onClick={handleNavClick('contact')}>Get in Touch</NavCTA>
          </div>
        )}

        {/* Mobile Hamburger */}
        {isMobile && (
          <button
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', gap: 5, padding: 8,
            }}
          >
            <span style={{ width: 24, height: 2, background: C.navy, display: 'block', transition: 'all 0.3s', transform: menuOpen ? 'rotate(45deg) translateY(7px)' : 'none' }} />
            <span style={{ width: 24, height: 2, background: C.navy, display: 'block', transition: 'all 0.3s', opacity: menuOpen ? 0 : 1 }} />
            <span style={{ width: 24, height: 2, background: C.navy, display: 'block', transition: 'all 0.3s', transform: menuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }} />
          </button>
        )}
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && isMobile && (
        <div style={{
          position: 'fixed', top: 80, left: 0, right: 0, bottom: 0, zIndex: 99,
          background: C.creamLight, padding: '40px 24px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {[['About', 'about'], ['Expertise', 'expertise'], ['Services', 'services']].map(([label, id]) => (
            <a key={id} href={`#${id}`} onClick={handleNavClick(id)}
              style={{
                padding: '16px 0', fontSize: 16, fontWeight: 600, letterSpacing: 1,
                textTransform: 'uppercase', color: C.navy, textDecoration: 'none',
                borderBottom: `1px solid ${C.creamWarm}`,
              }}
            >{label}</a>
          ))}
          <a href="/articles" onClick={() => { setMenuOpen(false); window.location.href = '/articles'; }}
            style={{
              padding: '16px 0', fontSize: 16, fontWeight: 600, letterSpacing: 1,
              textTransform: 'uppercase', color: C.navy, textDecoration: 'none',
              borderBottom: `1px solid ${C.creamWarm}`,
            }}
          >Articles</a>
          <a href="/publications" onClick={() => { setMenuOpen(false); window.location.href = '/publications'; }}
            style={{
              padding: '16px 0', fontSize: 16, fontWeight: 600, letterSpacing: 1,
              textTransform: 'uppercase', color: C.navy, textDecoration: 'none',
              borderBottom: `1px solid ${C.creamWarm}`,
            }}
          >Publications</a>
          <button
            onClick={handleNavClick('contact')}
            style={{
              marginTop: 16, padding: '16px 32px', background: C.blue, color: C.navy,
              border: 'none', fontFamily: "'Manrope',sans-serif", fontSize: 14,
              fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
              width: '100%',
            }}
          >Get in Touch</button>
        </div>
      )}
    </>
  );
}

function NavLink({ children, href, onClick }) {
  const [h, setH] = useState(false);
  return (
    <a href={href} onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        fontSize: 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase',
        color: h ? C.blue : C.textBody, textDecoration: 'none', transition: 'color 0.3s',
      }}
    >{children}</a>
  );
}

function NavCTA({ children, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        padding: '10px 28px', border: `1px solid ${C.blue}`,
        background: h ? C.blue : 'transparent',
        color: h ? C.navy : '#3366cc',
        fontFamily: "'Manrope',sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 2,
        textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s ease',
      }}
    >{children}</button>
  );
}

/* ═══════════════════════════════════════════
   HERO
═══════════════════════════════════════════ */
function Hero() {
  const isMobile = useMobile();
  return (
    <section id="hero" aria-label="Hero" style={{ minHeight: '100vh', background: C.navy, position: 'relative', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      {/* BG effects */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 800, height: 800, borderRadius: '50%', background: 'radial-gradient(circle, rgba(102,153,255,0.08) 0%, transparent 70%)', top: -200, right: -200, animation: 'float 12s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(102,153,255,0.04) 0%, transparent 70%)', bottom: -150, left: -100, animation: 'float 16s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(102,153,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(102,153,255,0.03) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 2, padding: isMobile ? '100px 20px 60px' : '140px 80px 100px', maxWidth: 1400, margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr', gap: isMobile ? 32 : 80, alignItems: 'center' }}>
        {/* Left */}
        <RevealLeft>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontSize: isMobile ? 9 : 11, fontWeight: 600, letterSpacing: isMobile ? 2 : 4, textTransform: 'uppercase', color: C.blue, marginBottom: isMobile ? 20 : 32 }}>
              <span style={{ width: isMobile ? 30 : 60, height: 1, background: C.blue, display: 'inline-block' }} />
              Independent Process Consultancy for the Iron & Steel Industry
            </div>
            <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 38 : 72, fontWeight: 300, lineHeight: 1.05, color: C.cream, letterSpacing: -1, marginBottom: isMobile ? 20 : 32 }}>
              Process Engineering<br />
              <strong style={{ fontWeight: 600, color: C.blue }}>Excellence</strong><br />
              in <em style={{ fontStyle: 'italic', fontWeight: 400, color: C.creamWarm }}>Iron & Steel & Beyond</em>
            </h1>
            <p style={{ fontSize: isMobile ? 14 : 16, lineHeight: 1.8, color: 'rgba(245,240,232,0.7)', maxWidth: 520, marginBottom: isMobile ? 32 : 48 }}>
              STEEL PROCESS CONSULTANCY Inc. delivers world-class technical services to the global iron & steel, ferro-alloys, cement, calcined lime, and foundry industries — powered by decades of senior-level expertise.
            </p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
              <BtnPrimary onClick={scrollTo('contact')}>Request Consultation</BtnPrimary>
              <BtnGhost onClick={scrollTo('services')}>Explore Services</BtnGhost>
            </div>
          </div>
        </RevealLeft>

        {/* Right – Stats Card */}
        <RevealRight delay={0.2}>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(102,153,255,0.1)', padding: isMobile ? 24 : 48, position: 'relative', backdropFilter: 'blur(10px)' }}>
            <div style={{ position: 'absolute', top: -1, left: 40, right: 40, height: 2, background: `linear-gradient(90deg, transparent, ${C.blue}, transparent)` }} />
            <div style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: C.blue, marginBottom: isMobile ? 20 : 28 }}>At a Glance</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? 20 : 32 }}>
              {[['30+', 'Years in Iron & Steel'], ['6', 'Core Industries'], ['20+', 'Countries Served'], ['100%', 'Independent']].map(([val, lbl]) => (
                <div key={lbl} style={{ paddingBottom: isMobile ? 16 : 24, borderBottom: '1px solid rgba(102,153,255,0.08)' }}>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 36 : 52, fontWeight: 600, color: C.cream, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: isMobile ? 10 : 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(245,240,232,0.65)', marginTop: 8 }}>{lbl}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: isMobile ? 20 : 32, paddingTop: isMobile ? 16 : 24, borderTop: '1px solid rgba(102,153,255,0.08)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.blue, animation: 'pulseGlow 2s ease infinite' }} />
              <span style={{ fontSize: isMobile ? 11 : 12, color: 'rgba(245,240,232,0.7)' }}>Accepting new process engineering consultancy engagements</span>
            </div>
          </div>
        </RevealRight>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   ABOUT
═══════════════════════════════════════════ */
function About() {
  const isMobile = useMobile();
  const industries = ['Iron & Steel', 'Ferro-Alloys', 'Cement', 'Lime Calcination', 'Foundry', 'Process Engineering'];
  return (
    <section id="about" style={{ background: C.creamLight, padding: isMobile ? '60px 20px' : '120px 80px', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent 10%, ${C.blue} 50%, transparent 90%)`, opacity: 0.3 }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '0.8fr 1.2fr', gap: isMobile ? 32 : 80, alignItems: 'start' }}>
        <RevealLeft>
          <SectionTag>Who We Are</SectionTag>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 32 : 48, fontWeight: 400, color: C.textDark, lineHeight: 1.15 }}>
            An Independent<br />Voice of <em style={{ fontStyle: 'italic', color: C.blueDark }}>Expertise</em>
          </h2>
        </RevealLeft>
        <RevealRight delay={0.15}>
          <p style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1.7, color: C.textDark, fontWeight: 500, marginBottom: 20 }}>
            STEEL PROCESS CONSULTANCY Inc. is an independent process consultancy firm providing technical services to the global iron and steel, ferro-alloys, cement, calcined lime / fluxes, and foundry industries.
          </p>
          <p style={{ fontSize: isMobile ? 14 : 16, lineHeight: 1.85, color: C.textBody, marginBottom: 36 }}>
            Our core team consists of highly experienced and globally recognized specialists & subject matter experts (SMEs) who have extensive experience working in senior positions in their field of expertise. We bring unbiased, data-driven insights to every engagement.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {industries.map(t => <IndustryTag key={t}>{t}</IndustryTag>)}
          </div>
        </RevealRight>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   EXPERTISE
═══════════════════════════════════════════ */
const expertiseData = [
  { num: 'i.', title: 'Global Process & Operations Experience', desc: 'Extensive experience across iron & steel, ferro-alloys, cement, lime calcination, foundry, process technologies, process engineering, project development for both brownfield and greenfield plants, and comprehensive plant operations support worldwide.' },
  { num: 'ii.', title: 'Technical Leadership, Problem Diagnosis & Operational Improvements', desc: 'Driving operating cost reduction, plant capacity & productivity enhancement, product quality improvement, decarbonization & energy efficiency in steel plants, environmental compliance, and digital transformation initiatives. Process engineering support for brownfield & greenfield projects, including technology selection and implementation.' },
  { num: 'iii.', title: 'Technical Support & Advisory', desc: 'Full-spectrum support spanning plant operations, process technology, engineering and projects, management and technical consulting, data analysis and problem diagnosis, research & development, and customised technical training programs.' },
  { num: 'iv.', title: 'Due Diligence & Turnaround Strategies', desc: 'Development and execution of technical and financial due diligence and turnaround strategies for individual steel plants as well as portfolios of plants within a given country or region.' },
];

function ExpertiseCard({ num, title, desc }) {
  const [h, setH] = useState(false);
  const isMobile = useMobile();
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        background: h ? 'rgba(102,153,255,0.04)' : 'rgba(255,255,255,0.02)',
        border: `1px solid ${h ? 'rgba(102,153,255,0.15)' : 'rgba(102,153,255,0.08)'}`,
        padding: isMobile ? '28px 20px' : '44px 40px', transition: 'all 0.5s ease', position: 'relative', overflow: 'hidden',
        transform: h ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: h ? '0 20px 60px rgba(0,0,0,0.2)' : 'none',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: h ? '100%' : 0, background: C.blue, transition: 'height 0.5s ease' }} />
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 16, fontStyle: 'italic', color: C.blue, marginBottom: 16, opacity: 0.7 }}>{num}</div>
      <h4 style={{ fontFamily: "'Manrope',sans-serif", fontSize: isMobile ? 16 : 18, fontWeight: 700, color: C.cream, marginBottom: 16 }}>{title}</h4>
      <p style={{ fontSize: isMobile ? 13 : 14, lineHeight: 1.8, color: 'rgba(245,240,232,0.7)' }}>{desc}</p>
    </div>
  );
}

function Expertise() {
  const isMobile = useMobile();
  return (
    <section id="expertise" style={{ background: C.navy, padding: isMobile ? '60px 20px' : '120px 80px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(102,153,255,0.05) 0%, transparent 60%)' }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 80 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: C.blue, marginBottom: 20 }}>Areas of Expertise</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 32 : 52, fontWeight: 400, color: C.cream, lineHeight: 1.15 }}>
              Deep Knowledge Across<br />the Entire Value Chain
            </h2>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 16 : 24 }}>
          {expertiseData.map((item, i) => (
            <Reveal key={i} delay={i * 0.1}><ExpertiseCard {...item} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   SERVICES
═══════════════════════════════════════════ */
const servicesData = [
  { marker: '01', title: 'Operational Improvements', desc: 'End-to-end operational improvement across all major process routes & technologies — from raw material preparation through to finished product. We evaluate & diagnose bottlenecks, model solutions, and implement practical & measurable improvements.', tags: ['Ore Beneficiation', 'Pelletizing', 'Sintering', 'Cokemaking', 'Direct Reduction', 'Blast Furnace', 'EAF / BOF / Induction Furnace', 'Secondary Steelmaking', 'Continuous Casting', 'Hot & Cold Rolling', 'Rotary Kilns', 'Shaft Furnaces', 'Fluidized Bed', 'Cupolas'] },
  { marker: '02', title: 'Plant Capacity and Operating Cost & Capex Optimization', desc: 'Strategic assessment and implementation of production capacity enhancement programs (involving no / low, medium & high capex initiatives) alongside operating cost reduction initiatives.', tags: ['Production Capacity Enhancement', 'Operating Cost Reduction', 'Value-in-Use Modelling'] },
  { marker: '03', title: 'Sustainability, Energy & Environment', desc: 'Comprehensive energy efficiency assessment and development of improvement programs (roadmap) combined with CO₂ emissions reduction strategies for short-, medium- and long-term.', tags: ['Energy Efficiency Assessment', 'CO₂ Emissions Reduction', 'Decarbonization Strategy'] },
  { marker: '04', title: 'Product Quality & Steel Cleanliness', desc: 'Targeted programs to improve cast & rolled product quality (surface & internal) and liquid steel cleanliness through evaluation of process parameters, inclusion control, and advanced monitoring.', tags: ['Product Quality Improvement', 'Liquid Steel Cleanliness', 'Cracks', 'Slivers'] },
  { marker: '05', title: 'Technology Evaluation & Implementation for Brownfield & Greenfield Projects', desc: 'Expert evaluation and selection of process technologies for new and existing plants, including FEL1 and FEL2 studies for greenfield & brownfield projects.', tags: ['Technology Evaluation & Implementation', 'FEL1 Conceptual Studies', 'FEL2 Pre-Feasibility', 'Brownfield & Greenfield Projects'] },
  { marker: '06', title: 'Customised Technical Training Programs', desc: `Custom-designed technical training programs for iron & steel processes, tailored to your team's requirements and your plant's specific operational context.`, tags: ['Custom Training & Knowledge Transfer', 'Iron & Steel Processes', 'Metallurgical Engineering'] },
];

function ServiceItem({ marker, title, desc, tags, isLast }) {
  const [h, setH] = useState(false);
  const isMobile = useMobile();
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '240px 1fr', gap: isMobile ? 16 : 48,
        padding: isMobile ? '32px 0' : '48px 0', paddingLeft: h && !isMobile ? 16 : 0,
        borderBottom: isLast ? 'none' : `1px solid ${C.creamWarm}`,
        transition: 'padding 0.3s ease',
      }}
    >
      <div>
        <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.blue, display: 'block', marginBottom: 8 }}>{marker}</span>
        <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 24 : 28, fontWeight: 600, color: C.textDark, lineHeight: 1.2 }}>{title}</span>
      </div>
      <div>
        <p style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.8, color: C.textBody, marginBottom: 16 }}>{desc}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {tags.map(t => (
            <span key={t} style={{ padding: '5px 14px', background: C.blueSub, border: '1px solid rgba(102,153,255,0.12)', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, color: C.blueDeep }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Services() {
  const isMobile = useMobile();
  return (
    <section id="services" style={{ background: C.creamLight, padding: isMobile ? '60px 20px' : '120px 80px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 20 : 60, alignItems: 'end', marginBottom: isMobile ? 40 : 80, paddingBottom: isMobile ? 24 : 40, borderBottom: `1px solid ${C.creamWarm}` }}>
            <div>
              <SectionTag>Technical Services</SectionTag>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 32 : 48, fontWeight: 400, color: C.textDark, lineHeight: 1.15 }}>Comprehensive Solutions for Every Process Stage</h2>
            </div>
            <p style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.75, color: C.textBody, textAlign: isMobile ? 'left' : 'right' }}>From raw material handling through finished product — our services cover the full spectrum of iron, steel, and associated industry processes.</p>
          </div>
        </Reveal>
        {servicesData.map((svc, i) => (
          <Reveal key={i} delay={i * 0.05}><ServiceItem {...svc} isLast={i === servicesData.length - 1} /></Reveal>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
   TRUST BAR
═══════════════════════════════════════════ */
function TrustMetric({ val, lbl }) {
  const [h, setH] = useState(false);
  const isMobile = useMobile();
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        textAlign: 'center', padding: isMobile ? '20px 12px' : '32px 20px',
        border: `1px solid ${h ? 'rgba(102,153,255,0.15)' : 'rgba(102,153,255,0.06)'}`,
        background: h ? 'rgba(102,153,255,0.03)' : 'rgba(255,255,255,0.01)',
        transition: 'all 0.4s ease',
      }}
    >
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 40 : 56, fontWeight: 600, color: C.blue, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: isMobile ? 10 : 11, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(245,240,232,0.65)', marginTop: 12, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{lbl}</div>
    </div>
  );
}

function TrustBar() {
  const isMobile = useMobile();
  const metrics = [['30+', 'Years Combined\nExperience'], ['20+', 'Countries\nServed'], ['6', 'Core Industry\nSectors'], ['100%', 'Independent &\nUnbiased']];
  return (
    <section style={{ background: C.navy, padding: isMobile ? '40px 20px' : 80, position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: isMobile ? 20 : 80, right: isMobile ? 20 : 80, height: 1, background: C.divider }} />
      <Reveal>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 12 : 40 }}>
          {metrics.map(([val, lbl]) => <TrustMetric key={lbl} val={val} lbl={lbl} />)}
        </div>
      </Reveal>
    </section>
  );
}

/* ═══════════════════════════════════════════
   CONTACT
═══════════════════════════════════════════ */
function Contact() {
  const isMobile = useMobile();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: false });
  };

    const handleSubmit = async () => {
    if (cooldown) { alert('Please wait before submitting again.'); return; }
    const newErrors = {};
    if (!form.name) newErrors.name = true;
    if (!form.email || !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = true;
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    setSaving(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-contact-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: form.name,
            company: form.company,
            email: form.email,
            phone: form.phone,
            service_interest: form.service,
            message: form.message,
          }),
        }
      );

      if (res.ok) {
    setSubmitted(true);
    setCooldown(true);
    setTimeout(() => setCooldown(false), 30000);
    } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      alert('Connection error. Please try again.');
    }

    setSaving(false);
  };

  const inputBase = (field) => ({
    width: '100%', padding: '14px 16px',
    border: `1px solid ${errors[field] ? '#e74c3c' : C.creamWarm}`,
    background: C.creamPale, fontFamily: "'Manrope',sans-serif", fontSize: 16,
    color: C.textDark, outline: 'none', transition: 'all 0.3s ease',
  });

  const focusStyle = (e) => { e.target.style.borderColor = C.blue; e.target.style.background = C.white; e.target.style.boxShadow = '0 0 0 3px rgba(102,153,255,0.08)'; };
  const blurStyle = (field) => (e) => { e.target.style.borderColor = errors[field] ? '#e74c3c' : C.creamWarm; e.target.style.background = C.creamPale; e.target.style.boxShadow = 'none'; };

  const labelSt = { display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textBody, marginBottom: 8 };

  const serviceOptions = [
    'Operational Improvements', 'Production Capacity Enhancement', 'Operating Cost Reduction',
    'Energy Efficiency & CO₂ Reduction', 'Product Quality / Steel Cleanliness',
    'Technology Evaluation & Selection', 'Greenfield Project Studies (FEL1/FEL2)',
    'Value-in-Use Modelling', 'Custom Training Programs', 'Due Diligence & Turnaround Strategy', 'General Inquiry',
  ];

  return (
    <section id="contact" style={{ background: C.creamLight, padding: isMobile ? '60px 20px' : '120px 80px', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent 10%, ${C.blue} 50%, transparent 90%)`, opacity: 0.2 }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.3fr', gap: isMobile ? 40 : 80, alignItems: 'start' }}>
        <RevealLeft>
          <SectionTag>Get in Touch</SectionTag>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 32 : 48, fontWeight: 400, color: C.textDark, lineHeight: 1.15, marginBottom: 24 }}>Start the<br />Conversation</h2>
          <p style={{ fontSize: isMobile ? 14 : 16, lineHeight: 1.8, color: C.textBody, marginBottom: isMobile ? 32 : 48 }}>
            {`Whether you're facing a specific operational challenge, planning a brownfield or greenfield project, or seeking a strategic technical partner for process engineering — our team is ready to bring technical clarity and expertise to address your situation.`}
          </p>
          {[['Email', 'contact@steelpcinc.com'], ['Phone', '+1 (647) 448-9176'], ['Global Reach', 'Serving clients across 20+ countries']].map(([label, value], i, arr) => (
            <div key={label} style={{ marginBottom: 28, paddingBottom: 28, borderBottom: i < arr.length - 1 ? `1px solid ${C.creamWarm}` : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.blue, marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: isMobile ? 15 : 17, color: C.textDark, fontWeight: 500 }}>{value}</div>
            </div>
          ))}
        </RevealLeft>

        <RevealRight delay={0.15}>
          <div style={{ background: C.white, padding: isMobile ? 24 : 56, border: `1px solid ${C.creamWarm}`, boxShadow: '0 8px 40px rgba(10,20,40,0.04)', position: 'relative' }}>
            <div style={{ position: 'absolute', top: -1, left: 40, right: 40, height: 2, background: `linear-gradient(90deg, transparent, ${C.blue}, transparent)` }} />

            {!submitted ? (
              <>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 24 : 28, fontWeight: 500, color: C.textDark, marginBottom: 8 }}>Request a Technical Consultation</h2>
                <p style={{ fontSize: 13, color: C.textMuted, marginBottom: isMobile ? 24 : 36 }}>Complete the form below and our team will respond within 24 hours.</p>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label htmlFor="name-input" style={labelSt}>Full Name *</label>
                    <input id="name-input" style={inputBase('name')} placeholder="Your full name" value={form.name} onChange={handleChange('name')} onFocus={focusStyle} onBlur={blurStyle('name')} aria-required="true" />
                  </div>
                  <div>
                    <label htmlFor="company-input" style={labelSt}>Company *</label>
                    <input id="company-input" style={inputBase('company')} placeholder="Company or organization" value={form.company} onChange={handleChange('company')} onFocus={focusStyle} onBlur={blurStyle('company')} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label htmlFor="email-input" style={labelSt}>Email Address *</label>
                    <input id="email-input" style={inputBase('email')} placeholder="your@email.com" type="email" value={form.email} onChange={handleChange('email')} onFocus={focusStyle} onBlur={blurStyle('email')} aria-required="true" />
                  </div>
                  <div>
                    <label htmlFor="phone-input" style={labelSt}>Phone</label>
                    <input id="phone-input" style={inputBase('phone')} placeholder="+1 (555) 000-0000" type="tel" value={form.phone} onChange={handleChange('phone')} onFocus={focusStyle} onBlur={blurStyle('phone')} />
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="service-select" style={labelSt}>Area of Interest *</label>
                  <select id="service-select" style={{ ...inputBase('service'), appearance: 'none', cursor: 'pointer', color: form.service ? C.textDark : C.textMuted }} value={form.service} onChange={handleChange('service')}>
                    <option value="" disabled>Select a service area</option>
                    {serviceOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="message-input" style={labelSt}>Project Background</label>
                  <textarea id="message-input" style={{ ...inputBase('message'), resize: 'none', height: 120 }} placeholder="Tell us about your project, challenge, or how we can help..." value={form.message} onChange={handleChange('message')} onFocus={focusStyle} onBlur={blurStyle('message')} />
                </div>

                <SubmitBtn onClick={handleSubmit}>{saving ? 'Sending...' : 'Submit Your Inquiry'}</SubmitBtn>
                <p style={{ textAlign: 'center', fontSize: 11, color: C.textMuted, marginTop: 16 }}>Your information will be confidential and will only be used to respond to your inquiry.</p>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: isMobile ? '40px 12px' : '60px 20px' }}>
                <div style={{ width: 64, height: 64, border: `2px solid ${C.blue}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28, color: C.blue }}>✓</div>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: isMobile ? 26 : 32, color: C.textDark, marginBottom: 12 }}>Inquiry Received</h2>
                <p style={{ fontSize: 14, color: C.textBody, lineHeight: 1.7 }}>Thank you for reaching out. A member of our team will review your inquiry and respond within 24 hours.</p>
              </div>
            )}
          </div>
        </RevealRight>
      </div>
    </section>
  );
}

function SubmitBtn({ children, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        width: '100%', padding: 18, background: h ? C.blueDark : C.blue, color: C.navy, border: 'none',
        fontFamily: "'Manrope',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 2.5,
        textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.4s ease', marginTop: 8,
        transform: h ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: h ? '0 12px 36px rgba(102,153,255,0.25)' : 'none',
      }}
    >{children}</button>
  );
}

/* ═══════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════ */
function Footer() {
  const isMobile = useMobile();
  return (
    <footer style={{ background: C.navy, padding: isMobile ? '48px 20px 32px' : '80px 80px 40px', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: isMobile ? 20 : 80, right: isMobile ? 20 : 80, height: 1, background: C.divider }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr 1fr', gap: isMobile ? 32 : 60, paddingBottom: isMobile ? 32 : 60, borderBottom: '1px solid rgba(102,153,255,0.06)' }}>
        <div>
          <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.cream, marginBottom: 20 }}>
            STEEL PROCESS <span style={{ color: C.blue }}>CONSULTANCY</span>
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(245,240,232,0.4)', maxWidth: 340 }}>Independent process consultancy providing world-class technical services to the global iron & steel and allied industries.</p>
        </div>
        <FooterCol title="Services" links={[['Operational Improvements', '#services'], ['Energy & Environment', '#services'], ['Process Technology', '#services'], ['Brownfield & Greenfield Projects', '#services'], ['Custom Training Programs', '#services']]} />
        <FooterCol title="Company" links={[['About Us', '#about'], ['Expertise', '#expertise'], ['Contact', '#contact']]} />
      </div>
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingTop: isMobile ? 20 : 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 8 : 0 }}>
        <p style={{ fontSize: 12, color: 'rgba(245,240,232,0.25)' }}>© 2026 Steel Process Consultancy Inc. All rights reserved.</p>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.blue, marginBottom: 24 }}>{title}</h4>
      {links.map(([label, href]) => <FooterLink key={label} href={href}>{label}</FooterLink>)}
    </div>
  );
}

function FooterLink({ children, href = '#' }) {
  const [h, setH] = useState(false);
  return (
    <a href={href} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: 'block', fontSize: 14, color: h ? C.cream : 'rgba(245,240,232,0.5)', textDecoration: 'none', marginBottom: 14, transition: 'color 0.3s' }}
    >{children}</a>
  );
}

/* ═══════════════════════════════════════════
   BACK TO TOP
═══════════════════════════════════════════ */
function BackToTop() {
  const [visible, setVisible] = useState(false);
  const isMobile = useMobile();

  useEffect(() => {
    const fn = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <button
      onClick={scrollTop}
      style={{
        position: 'fixed',
        bottom: isMobile ? 20 : 40,
        right: isMobile ? 20 : 40,
        width: isMobile ? 40 : 48,
        height: isMobile ? 40 : 48,
        background: C.blue,
        color: C.navy,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isMobile ? 18 : 20,
        fontWeight: 700,
        cursor: 'pointer',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.4s ease',
        boxShadow: '0 8px 24px rgba(102,153,255,0.3)',
        zIndex: 50,
      }}
    >
      ↑
    </button>
  );
}

/* ═══════════════════════════════════════════
   LANDING PAGE
═══════════════════════════════════════════ */
export default function LandingPage() {
  return (
    <div style={{ background: C.navy }}>
      <a href="#about" className="skip-link">Skip to content</a>
      <Nav />
      <Hero />
      <About />
      <Expertise />
      <Services />
      <TrustBar />
      <Contact />
      <Footer />
      <BackToTop />
    </div>
  );
}